import type { Program, ProgramStatus } from './ProgramsPage.component';
import type { ProgramListEntry } from './programs-query';
import { computeProgramDaysRemaining } from './program-row-format';

/**
 * Translate Core's `status.key` into the UI's status taxonomy.
 *
 *   not_published → rascunho
 *   private       → ativo-privado
 *   published     → ativa
 *   cancelled     → cancelado
 *   inactivated   → encerrado
 *   expired       → encerrado  (derived in the BFF: published/private program
 *                               past its `endApplying` window — see the
 *                               recruiter BFF `program-status.ts`)
 */
function mapStatus(statusKey: ProgramListEntry['statusKey']): ProgramStatus {
  switch (statusKey) {
    case 'not_published':
      return 'rascunho';
    case 'private':
      return 'ativo-privado';
    case 'published':
      return 'ativa';
    case 'cancelled':
      return 'cancelado';
    case 'inactivated':
      return 'encerrado';
    case 'expired':
      return 'encerrado';
  }
}

/**
 * dd/MM/yy in America/Sao_Paulo — the window columns are now UTC instants
 * (`timestamptz`), so we must render the São Paulo calendar day, not the UTC
 * one (an evening-BRT deadline is the next UTC day → would show the wrong date).
 */
const DATE_BR_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  timeZone: 'America/Sao_Paulo',
});

function formatDateBR(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return DATE_BR_FORMAT.format(date);
}

export function mapProgramRow(entry: ProgramListEntry): Program {
  const status = mapStatus(entry.statusKey);
  return {
    // Real UUID — shown in the ID column AND passed to mutations
    // (duplicate, edit, publish, ...).
    id: entry.id,
    name: entry.name,
    // EU-241 — company logo (null → placeholder icon in the row). The BFF
    // schema is `.nullish()` (key omitted upstream ⇒ undefined) — coalesce.
    logoUrl: entry.companyLogoUrl ?? null,
    status,
    jobsCount: entry.jobsCount,
    registrationsCount: entry.registrationsCount,
    startDate: formatDateBR(entry.startApplying),
    endDate: formatDateBR(entry.endApplying),
    // EU-405 — badge "N dias" = dias restantes até o fim (`endApplying`),
    // contagem inclusiva BRT, recalculada por dia a partir do relógio do
    // cliente. Substitui o `daysOnAir` (EU-238), que contava da data errada
    // (`startDate`/`publishedAt`) e exibia "0 dias" para todos.
    daysRemaining: computeProgramDaysRemaining(entry.endApplying, status, new Date()),
  };
}
